import GiftOrderApi from '@apis/gift/GiftOrderApi';
import useFingerPay from '@hooks/order/useFingerPay';
import useNicePay from '@hooks/order/useNicePay';
import useProject from '@hooks/useProject';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { GiftItemRow, GiftOrderRequest } from '@models/gift';
import GiftOrderContext from '@providers/GiftOrderProvider/GiftOrderContext';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

function useGiftOrder(giftItem: GiftItemRow, qty: number, giftCardImageId?: string) {
    const { state, enabled } = useContext(GiftOrderContext);
    const projectId = useProjectId();
    const openFingerPay = useFingerPay();
    const openNicePay = useNicePay();
    const project = useProject();
    const [, setCookie] = useCookies(['giftOrder']);

    const { t } = useTranslation('gift');
    const submitting = useRef(false);
    const { mutate, isPending } = useMutation({
        mutationFn: GiftOrderApi.createGiftOrder,
        onSuccess(res, request) {
            const {
                pageUrl, orderNo, mallId, requestedAt, hashString, returnUrl,
            } = res;
            if (pageUrl) {
                window.location.href = pageUrl;
            } else if (orderNo && mallId && requestedAt && hashString && returnUrl) {
                const { amount } = request;
                const itemName = t('giftOrder.giftOrderItem', { name: giftItem.name, count: request.giftRecipients.length });
                if (project?.pgVendor === 'FINGERPAY') {
                    openFingerPay({
                        paymentMethod: 'CREDIT_CARD',
                        orderNo,
                        mallId,
                        requestedAt,
                        hashString,
                        paymentAmount: amount,
                        itemName,
                        returnUri: returnUrl,
                    });
                } else if (project?.pgVendor === 'NICEPAY') {
                    openNicePay({
                        paymentAmount: amount,
                        itemName,
                        returnUri: returnUrl,
                        orderNo,
                        mallId,
                        requestedAt,
                        hashString,
                    });
                } else {
                    toast.error('pg 설정을 확인해주세요.');
                }
            }
        },
        onError(e: AxiosError<ErrorResponse>) {
            console.error(e);
            toast.error(e.response?.data?.message || t('giftOrder.errorMessage'));
        },
        onSettled() {
            submitting.current = false;
        },
    });
    const handleOrder = () => {
        if (!enabled || submitting.current || isPending) {
            return;
        }
        const {
            message, userPhone, userName, giftRecipients, cardName,
        } = state;
        const amount = giftItem.salePrice * qty * giftRecipients.length;
        const request: GiftOrderRequest = {
            projectId,
            userName,
            userPhone: userPhone.replace(/-/gi, ''),
            message: giftCardImageId ? undefined : (message?.trim() || t('giftOrder.messagePlaceholder')),
            giftRecipients: giftRecipients.map((it) => ({
                name: it.name,
                phoneNo: it.phoneNo.replace(/-/gi, ''),
            })),
            paymentMethod: 'CREDIT_CARD',
            amount,
            giftOrderItems: [{ giftItemId: giftItem.id, count: qty }],
            appScheme: 'com.waldlust.platform',
            cardName,
            giftCardImageId,
        };
        submitting.current = true;
        setCookie('giftOrder', JSON.stringify(request), { path: '/', maxAge: 60 * 5 });
        mutate(request);
    };
    return {
        handleOrder, state, enabled, isPending,
    };
}

export default useGiftOrder;
